import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { handleApiError } from 'src/app/helper/helper';
import { Player } from 'src/app/interfaces/player.interfaces';
import { ApiService } from 'src/app/services/api.service';

type PlayerSortKey =
  | 'points'
  | 'pointsResearch'
  | 'pointsSociety'
  | 'pointsEconomy'
  | 'pointsMilitary'
  | 'reputation'
  | 'level'
  | 'ticks'
  | 'id';
type SortDirection = 'asc' | 'desc';

@Component({
  standalone: false,
  selector: 'app-player-list',
  templateUrl: './player-list.component.html',
})
export class PlayerListComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  players: Player[] | undefined;
  showAll = true;
  sortKey: PlayerSortKey = 'id';
  sortDirection: SortDirection = 'asc';

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.subscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.showAll = queryParams.get('showActive') === 'true' ? false : true;
      const sort = queryParams.get('sort');
      const direction = queryParams.get('dir');
      this.sortKey = this.isSortKey(sort) ? sort : 'id';
      this.sortDirection =
        direction === 'desc' || direction === 'asc' ? direction : 'asc';
      this.loadPlayers();
    });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  async loadPlayers() {
    try {
      if (this.showAll) {
        this.players = await this.apiService.fetchPlayers();
      } else {
        this.players = await this.apiService.fetchActivePlayers();
      }
      this.sortPlayers();
    } catch (error) {
      handleApiError(this.authService, this.router, error);
    }
  }

  setSort(sortKey: PlayerSortKey): void {
    const sortDirection =
      this.sortKey === sortKey
        ? this.toggleDirection()
        : this.defaultDirection(sortKey);
    this.router.navigate(['/player/list'], {
      queryParams: {
        showActive: this.showAll ? undefined : true,
        sort: sortKey,
        dir: sortDirection,
      },
    });
  }

  getSortLabel(sortKey: PlayerSortKey): string {
    if (this.sortKey !== sortKey) {
      return '';
    }
    return this.sortDirection === 'asc' ? 'aufsteigend' : 'absteigend';
  }

  getSortMarker(sortKey: PlayerSortKey): string {
    if (this.sortKey !== sortKey) {
      return '';
    }
    return this.sortDirection === 'asc' ? '^' : 'v';
  }

  getTotalPoints(player: Player): number {
    return (
      player.points.research +
      player.points.society +
      player.points.economy +
      player.points.military
    );
  }

  private sortPlayers(): void {
    if (!this.players) {
      return;
    }
    const directionFactor = this.sortDirection === 'asc' ? 1 : -1;
    this.players = [...this.players].sort((a, b) => {
      const valueCompare =
        (this.getSortValue(a, this.sortKey) -
          this.getSortValue(b, this.sortKey)) *
        directionFactor;
      if (valueCompare !== 0) {
        return valueCompare;
      }
      return a.id - b.id;
    });
  }

  private getSortValue(player: Player, sortKey: PlayerSortKey): number {
    switch (sortKey) {
      case 'points':
        return this.getTotalPoints(player);
      case 'pointsResearch':
        return player.points.research;
      case 'pointsSociety':
        return player.points.society;
      case 'pointsEconomy':
        return player.points.economy;
      case 'pointsMilitary':
        return player.points.military;
      default:
        return player[sortKey];
    }
  }

  private toggleDirection(): SortDirection {
    return this.sortDirection === 'asc' ? 'desc' : 'asc';
  }

  private defaultDirection(sortKey: PlayerSortKey): SortDirection {
    return sortKey === 'id' ? 'asc' : 'desc';
  }

  private isSortKey(sortKey: string | null): sortKey is PlayerSortKey {
    return [
      'points',
      'pointsResearch',
      'pointsSociety',
      'pointsEconomy',
      'pointsMilitary',
      'reputation',
      'level',
      'ticks',
      'id',
    ].includes(sortKey ?? '');
  }
}
